import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Users, BarChart3, ShieldCheck, Check } from 'lucide-react';
import MarketingNav from '@/components/MarketingNav';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Corporate = () => {
  const [numCards, setNumCards] = useState(25);
  const [quote, setQuote] = useState(null);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/corporate/pricing-tiers`).then((r) => setTiers(r.data.tiers));
  }, []);

  useEffect(() => {
    if (numCards < 10) {
      setQuote(null);
      return;
    }
    const t = setTimeout(() => {
      axios
        .post(`${BACKEND_URL}/api/corporate/pricing-quote`, { num_cards: Number(numCards) })
        .then((r) => setQuote(r.data))
        .catch(() => setQuote(null));
    }, 300);
    return () => clearTimeout(t);
  }, [numCards]);

  return (
    <div className="min-h-screen bg-[#F5F1EA] text-[#0A0A0A]" data-testid="corporate-landing">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 lg:pt-40 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A0A0A]/5 border border-[#0A0A0A]/10 mb-8">
            <Building2 className="w-3.5 h-3.5 text-[#B8935A]" />
            <span className="text-xs font-medium text-[#0A0A0A]">Sol Digital for Teams</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-8xl font-heading font-bold tracking-tight leading-[0.95] mb-6">
            One brand.<br />
            <span className="text-[#B8935A]">Every employee.</span>
          </h1>
          <p className="text-lg lg:text-xl text-[#525252] max-w-2xl mb-10 leading-relaxed">
            Roll out digital business cards across your company in minutes. Centralised branding, employee
            stats, bulk management — built for sales teams, agencies and growing businesses.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/corporate/signup" data-testid="cta-corporate-signup">
              <Button className="h-14 px-8 rounded-full bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white font-semibold shadow-xl shadow-black/10">
                Join your company
              </Button>
            </Link>
            <a href="mailto:support@soldigital.in?subject=Corporate%20Account%20Request">
              <Button variant="outline" className="h-14 px-8 rounded-full bg-transparent border-[#0A0A0A]/20 text-[#0A0A0A] hover:bg-[#0A0A0A]/5 font-semibold" data-testid="cta-contact-sales">
                Register your company
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              icon: Users,
              title: 'Bulk employee management',
              body: 'Add or remove employee cards from a single dashboard. New hires onboard themselves with their work email.',
            },
            {
              icon: ShieldCheck,
              title: 'Brand consistency',
              body: 'Admin sets the company logo, description and branding. Employees customise the rest of their card.',
            },
            {
              icon: BarChart3,
              title: 'Team analytics',
              body: 'See views, leads and top performers across your entire team. Measure your card ROI in real time.',
            },
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-8 border border-[#0A0A0A]/10 hover:border-[#B8935A]/40 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] flex items-center justify-center mb-6">
                <f.icon className="w-5 h-5 text-[#B8935A]" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-2">{f.title}</h3>
              <p className="text-[#525252] leading-relaxed text-sm">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-[#EBE6DC]/60 border-y border-[#0A0A0A]/10" data-testid="corp-pricing-section">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Volume pricing</p>
            <h2 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight mb-3">
              The bigger your team, the better the rate.
            </h2>
            <p className="text-[#525252]">Minimum order: 10 cards.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mb-12">
            {tiers.map((tier, idx) => (
              <div
                key={tier.name}
                className={`rounded-2xl p-8 border transition-all ${idx === 1 ? 'bg-[#0A0A0A] text-white border-[#0A0A0A] lg:scale-105 shadow-2xl shadow-black/20' : 'bg-white border-[#0A0A0A]/10 text-[#0A0A0A]'}`}
                data-testid={`tier-${tier.name.toLowerCase()}`}
              >
                <p className="text-xs uppercase tracking-widest mb-3 font-semibold text-[#B8935A]">
                  {tier.name}
                </p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className={`text-5xl font-heading font-bold ${idx === 1 ? 'text-white' : 'text-[#0A0A0A]'}`}>₹{tier.rate_per_card}</span>
                  <span className={`text-sm ${idx === 1 ? 'text-white/50' : 'text-[#525252]'}`}>/card/yr</span>
                </div>
                <p className={`text-sm mb-8 ${idx === 1 ? 'text-white/70' : 'text-[#525252]'}`}>
                  {tier.max ? `${tier.min}–${tier.max} cards` : `${tier.min}+ cards`}
                </p>
                <ul className={`space-y-2.5 text-sm ${idx === 1 ? 'text-white/85' : 'text-[#525252]'}`}>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#B8935A]" />
                    <span>iOS / Android compatible vCards</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#B8935A]" />
                    <span>Centralised admin dashboard</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#B8935A]" />
                    <span>Team analytics &amp; leads</span>
                  </li>
                </ul>
              </div>
            ))}
          </div>

          {/* Calculator */}
          <div className="bg-white rounded-2xl p-6 lg:p-8 border border-[#0A0A0A]/10 max-w-3xl">
            <h3 className="font-heading font-bold text-xl mb-5">Estimate your spend</h3>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 w-full">
                <Label className="text-[#0A0A0A] mb-2 block text-sm font-medium">Number of cards</Label>
                <Input
                  type="number"
                  min={10}
                  value={numCards}
                  onChange={(e) => setNumCards(e.target.value)}
                  className="h-12 rounded-xl border-[#0A0A0A]/15 bg-white"
                  data-testid="pricing-calc-input"
                />
              </div>
              {quote && (
                <div className="flex-1 bg-[#F5F1EA] rounded-xl p-4 border border-[#B8935A]/30 w-full" data-testid="pricing-calc-result">
                  <p className="text-xs text-[#B8935A] uppercase tracking-widest mb-1 font-semibold">{quote.tier_name} · ₹{quote.rate_per_card}/card</p>
                  <p className="text-3xl font-heading font-bold text-[#0A0A0A]">₹{quote.total.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-[#525252] mt-1">per year, for {quote.num_cards} cards</p>
                </div>
              )}
              {!quote && numCards < 10 && (
                <p className="text-sm text-red-600">Minimum 10 cards required</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-6xl font-heading font-bold tracking-tight leading-[0.95] mb-6">
            Ready to <span className="text-[#B8935A]">go corporate?</span>
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto leading-relaxed">
            If your company is already approved, sign up with your work email. Otherwise, ping us and we'll
            onboard your domain in under 24 hours.
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/corporate/signup">
              <Button className="h-14 px-8 rounded-full bg-[#B8935A] hover:bg-[#a3824c] text-black font-semibold">
                Sign up with work email
              </Button>
            </Link>
            <a href="mailto:support@soldigital.in?subject=Corporate%20Account%20Request">
              <Button variant="outline" className="h-14 px-8 rounded-full bg-transparent border-white/20 text-white hover:bg-white/10 font-semibold">
                Contact sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Corporate;
